import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { 
  AnnouncementData, 
  AnnouncementModal, 
  shouldShowAnnouncement, 
  markAnnouncementAsShown 
} from "../components/AnnouncementModal";
import { apiService } from "../services/api";

// ============ CONTEXT INTERFACE ============

interface AnnouncementContextType {
  currentAnnouncement: AnnouncementData | null;
  announcements: AnnouncementData[];
  showAnnouncement: (announcement: AnnouncementData) => void;
  dismissAnnouncement: () => void;
  fetchAnnouncements: () => Promise<void>;
  isLoading: boolean;
  setNavigationCallback: (callback: (screenId: string) => void) => void;
  setUserName: (name: string) => void;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

// ============ MOCK DATA FOR DEVELOPMENT ============
// This will be replaced by actual API calls in production

const mockAnnouncements: AnnouncementData[] = [
  {
    id: "welcome-daily-2024",
    type: "welcome",
    title: "Welcome to Garage 360!",
    subtitle: "Your complete garage management solution",
    description: "Start your day with powerful tools to manage vehicles, inspections, job cards, inventory, and more. Explore our latest features to boost your productivity!",
    media: {
      type: "image",
      url: "/src/images/ad_1.png",
      altText: "Garage 360 Logo",
    },
    primaryCta: {
      text: "Start App Tour",
      navigateTo: "start-tour", // Special action
    },
    secondaryCta: {
      text: "Go to Dashboard",
      navigateTo: "home",
    },
    display: {
      showOncePerDay: true,
    },
    priority: 1,
    enabled: true,
  },
  // Example feature announcement
  {
    id: "feature-card-view-2024",
    type: "feature",
    title: "Introducing Card View!",
    subtitle: "A fresh way to see your data",
    description: "Switch between Table and Card views on any data screen. Card view provides a visual overview with status badges, progress bars, and quick actions. Try it on the Inspection page!",
    media: {
      type: "image",
      url: "/images/feature-card-view.png",
      altText: "Card View Feature",
    },
    primaryCta: {
      text: "Try Card View",
      navigateTo: "103", // Inspection page
    },
    display: {
      showOnceEver: true,
      startDate: "2024-01-01",
    },
    priority: 2,
    enabled: false, // Set to true to enable
  },
  // Example promotion
  {
    id: "promo-upgrade-2024",
    type: "promotion",
    title: "Upgrade to Premium!",
    subtitle: "50% off for a limited time",
    description: "Get access to advanced reporting, multi-branch support, and priority customer support. Offer ends soon!",
    primaryCta: {
      text: "Learn More",
      navigateTo: "https://garage360.com/pricing",
      isExternal: true,
    },
    display: {
      showOncePerDay: true,
      startDate: "2024-01-01",
      endDate: "2024-12-31",
    },
    priority: 3,
    enabled: false, // Set to true to enable
  },
];

// ============ API ENDPOINT CONFIGURATION ============
// The backend should implement this endpoint to return announcements

const ANNOUNCEMENTS_ENDPOINT = "/api/announcements"; // Backend endpoint

// ============ PROVIDER ============

export const AnnouncementProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<AnnouncementData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [navigationCallback, setNavigationCallbackState] = useState<((screenId: string) => void) | null>(null);
  const [hasCheckedAnnouncements, setHasCheckedAnnouncements] = useState(false);
  const [userName, setUserNameState] = useState<string>("");

  // Set navigation callback
  const setNavigationCallback = useCallback((callback: (screenId: string) => void) => {
    setNavigationCallbackState(() => callback);
  }, []);

  // Set user name for personalized greetings
  const setUserName = useCallback((name: string) => {
    setUserNameState(name);
  }, []);

  // Fetch announcements from backend
  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    try {
      // Try to fetch from backend
      const response = await apiService.get<AnnouncementData[]>(ANNOUNCEMENTS_ENDPOINT);
      if (response && Array.isArray(response)) {
        setAnnouncements(response);
        return;
      }
    } catch (error) {
      console.log("[Announcements] Backend not available, using mock data");
    }
    
    // Fallback to mock data for development
    setAnnouncements(mockAnnouncements);
    setIsLoading(false);
  }, []);

  // Show a specific announcement
  const showAnnouncement = useCallback((announcement: AnnouncementData) => {
    setCurrentAnnouncement(announcement);
  }, []);

  // Dismiss current announcement
  const dismissAnnouncement = useCallback(() => {
    setCurrentAnnouncement(null);
    // Dispatch event to prevent auto-start tour
    window.dispatchEvent(new CustomEvent("announcement-dismissed"));
  }, []);

  // Handle navigation from announcement CTA
  const handleNavigate = useCallback((target: string) => {
    if (target === "start-tour") {
      // Special action to start the app tour
      // Set flag to indicate tour is being intentionally started (before closing modal)
      (window as any).__tourStarting = true;
      // Close announcement without dispatching dismissal event
      setCurrentAnnouncement(null);
      // Mark announcement as shown
      markAnnouncementAsShown("welcome-daily-2024");
      // Small delay to ensure modal closes before tour starts
      setTimeout(() => {
        // Dispatch custom event for tour start
        window.dispatchEvent(new CustomEvent("start-app-tour"));
        // Reset flag after tour starts
        setTimeout(() => {
          (window as any).__tourStarting = false;
        }, 1000);
      }, 100);
      return;
    }
    
    // For other actions (like "Go to Dashboard"), prevent tour
    if (navigationCallback) {
      navigationCallback(target);
    }
    dismissAnnouncement();
  }, [navigationCallback, dismissAnnouncement]);

  // Check and show announcements on mount
  useEffect(() => {
    if (announcements.length > 0 && !hasCheckedAnnouncements && !currentAnnouncement) {
      // Find the first announcement that should be shown
      // Sort by priority (higher first) and filter by display rules
      const sortedAnnouncements = [...announcements]
        .filter((a) => a.enabled !== false)
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));

      for (const announcement of sortedAnnouncements) {
        if (shouldShowAnnouncement(announcement)) {
          // Small delay to allow the app to fully render
          setTimeout(() => {
            showAnnouncement(announcement);
          }, 1000);
          break;
        }
      }
      setHasCheckedAnnouncements(true);
    }
  }, [announcements, hasCheckedAnnouncements, currentAnnouncement, showAnnouncement]);

  return (
    <AnnouncementContext.Provider
      value={{
        currentAnnouncement,
        announcements,
        showAnnouncement,
        dismissAnnouncement,
        fetchAnnouncements,
        isLoading,
        setNavigationCallback,
        setUserName,
      }}
    >
      {children}
      
      {/* Announcement Modal */}
      <AnnouncementModal
        announcement={currentAnnouncement}
        onClose={dismissAnnouncement}
        onNavigate={handleNavigate}
        userName={userName}
      />
    </AnnouncementContext.Provider>
  );
};

// ============ HOOK ============

export const useAnnouncement = (): AnnouncementContextType => {
  const context = useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error("useAnnouncement must be used within an AnnouncementProvider");
  }
  return context;
};
